import type { InsightQuery, Me, PersonInsight } from '@smarton-pbx/shared';
import { GraduationCap, Lightbulb } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../api';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { EmptyState, Loading, Notice, PageHeader, Pill } from '../components/ui';
import { locale, message, t, tp } from '../i18n';
import { useLoad } from '../lib/hooks';
import { when } from './Calls';

function fromDate(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days + 1);
  return date.toLocaleDateString('sv');
}

const percent = (value: number | null) => (value === null ? '—' : `${Math.round(value * 100)}%`);

/** Horizontal bars; each bar opens the calls behind it. */
function Bars({ items, link, testId }: { items: { key: string; name: string; count: number }[]; link?: (key: string) => string | null; testId: string }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <p className="subtle">{t('Nothing yet in this period.')}</p>;
  return (
    <ul className="hbars" data-testid={testId}>
      {items.map((item) => {
        const to = link?.(item.key) ?? null;
        const content = (
          <>
            <span className="hbar-label">{item.name}</span>
            <span className="hbar-track">
              <span className="hbar-fill" style={{ width: `${(item.count / max) * 100}%` }} />
            </span>
            <span className="hbar-count tabular">{item.count}</span>
          </>
        );
        return <li key={item.key}>{to ? <Link to={to} title={t('See these calls')}>{content}</Link> : <span>{content}</span>}</li>;
      })}
    </ul>
  );
}

function Trend({ person }: { person: PersonInsight }) {
  const points = person.weeks.filter((w) => w.staffShare !== null);
  if (points.length < 2) return <span className="subtle">—</span>;
  return (
    <span className="spark" title={points.map((w) => `${w.weekStart}: ${percent(w.staffShare)}`).join('\n')}>
      {points.map((w) => (
        <span key={w.weekStart} style={{ height: `${Math.max(8, (w.staffShare ?? 0) * 100)}%` }} />
      ))}
    </span>
  );
}

/** `own`: an agent sees only themselves, as "You". */
function insightColumns(own: boolean): Column<PersonInsight>[] {
  return [
    {
      key: 'person',
      label: own ? t('You') : t('Person'),
      fixed: true,
      cell: (p) => (
        <Link to={`/calls?userId=${p.userId}`} className="plain-link">
          {p.name}
        </Link>
      ),
      value: (p) => p.name,
    },
    { key: 'reviewed', label: t('Reviewed'), className: 'num tabular', cell: (p) => p.reviewed, value: (p) => p.reviewed },
    { key: 'sales', label: t('Sales'), className: 'num tabular', cell: (p) => p.sales, value: (p) => p.sales },
    {
      key: 'talkShare',
      label: t('Talk share'),
      hint: t('Share of the talking done by the salesperson'),
      className: 'num tabular',
      cell: (p) => percent(p.staffShare),
      // A share, so Excel shows it as a percentage.
      value: (p) => p.staffShare,
      excelFormat: '0%',
    },
    {
      key: 'trend',
      label: t('Trend'),
      className: 'hide-mobile',
      cell: (p) => <Trend person={p} />,
      value: (p) =>
        p.weeks
          .filter((w) => w.staffShare !== null)
          .map((w) => `${w.weekStart}: ${percent(w.staffShare)}`)
          .join(', '),
    },
    {
      key: 'monologue',
      label: t('Longest monologue'),
      hint: t('Longest stretch without letting the customer speak'),
      className: 'num tabular hide-mobile',
      cell: (p) => (p.longestMonologueSeconds === null ? '—' : t('{n} s', { n: p.longestMonologueSeconds })),
      excel: [{ label: `${t('Longest monologue')} (s)`, value: (p) => p.longestMonologueSeconds }],
    },
    { key: 'questions', label: t('Questions per call'), className: 'num tabular hide-mobile', cell: (p) => p.questionsPerCall ?? '—', value: (p) => p.questionsPerCall },
    {
      key: 'coaching',
      label: t('Coaching points'),
      className: 'hide-mobile coaching',
      cell: (p) => (p.coaching.length ? p.coaching.slice(0, 2).join(' · ') : <span className="subtle">—</span>),
      value: (p) => p.coaching.join(' · '),
    },
  ];
}

export function Insights({ me }: { me: Me }) {
  const [days, setDays] = useState(30);
  const [regionId, setRegionId] = useState('');
  const regions = useLoad(() => (me.role === 'TENANT_ADMIN' ? api.listRegions() : Promise.resolve([])), [me.role]);
  const query: InsightQuery = { from: fromDate(days), ...(regionId && { regionId }) };
  const reasons = useLoad(() => api.insightReasons(query), [days, regionId]);
  const people = useLoad(() => api.insightPeople(query), [days, regionId]);
  const digests = useLoad(api.listDigests);
  const own = me.role === 'AGENT';
  const table = useTable('insights', insightColumns(own), mayDownload(me.role) ? { name: t('Insights'), rows: () => people.data ?? [] } : null);

  return (
    <div className="stack">
      <PageHeader
        title={t('Insights')}
        description={own ? t('What the AI reviews of your sales calls add up to.') : t('Why sales are lost, what customers object to, and how each person is doing.')}
      />
      <div className="card filters">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t('Period')}>
          <option value={7}>{t('Last 7 days')}</option>
          <option value={30}>{t('Last 30 days')}</option>
          <option value={90}>{t('Last 90 days')}</option>
          <option value={365}>{t('Last year')}</option>
        </select>
        {me.role === 'TENANT_ADMIN' && (regions.data?.length ?? 0) > 1 && (
          <select value={regionId} onChange={(e) => setRegionId(e.target.value)} aria-label={t('Region')}>
            <option value="">{t('All regions')}</option>
            {regions.data?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
        <span className="subtle grow" style={{ textAlign: 'right', fontSize: '0.85rem' }}>
          {t('From AI reviews: guesses, corrected by people where they knew better.')}
        </span>
      </div>
      {(reasons.error ?? people.error) && <Notice kind="error">{reasons.error ?? people.error}</Notice>}
      {!reasons.data ? (
        <Loading />
      ) : reasons.data.reviewed === 0 ? (
        <div className="card">
          <EmptyState icon={Lightbulb} title={t('No reviewed sales calls yet')}>
            <p>{t('Once AI review is on (Settings → AI call review), sales calls are reviewed after they are transcribed.')}</p>
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="stat-tiles">
            <div className="card tile">
              <span className="tile-label">{t('Reviewed sales calls')}</span>
              <span className="tile-value tabular" data-testid="reviewed-count">
                {reasons.data.reviewed}
              </span>
            </div>
            <div className="card tile">
              <span className="tile-label">{t('Sale agreed')}</span>
              <span className="tile-value tabular">{reasons.data.outcomes.SALE_AGREED}</span>
            </div>
            <div className="card tile">
              <span className="tile-label">{t('Follow-up agreed')}</span>
              <span className="tile-value tabular">{reasons.data.outcomes.FOLLOW_UP_AGREED}</span>
            </div>
            <div className="card tile">
              <span className="tile-label">{t('No sale')}</span>
              <span className="tile-value tabular">{reasons.data.outcomes.NO_SALE}</span>
            </div>
          </div>
          <div className="insight-grid">
            <div className="card">
              <div className="card-header">
                <h2>{t('Why sales are lost')}</h2>
              </div>
              <div className="card-body">
                <Bars
                  testId="lost-reasons"
                  items={reasons.data.reasons.map((r) => ({ key: r.categoryId ?? 'other', name: message(r.name), count: r.count }))}
                  link={(key) => (key === 'other' ? null : `/calls?reasonCategoryId=${key}`)}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>{t('Objections')}</h2>
              </div>
              <div className="card-body">
                <Bars testId="objections" items={reasons.data.objections.map((r) => ({ key: r.categoryId ?? 'other', name: message(r.name), count: r.count }))} />
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h2>{t('Competitors named')}</h2>
              </div>
              <div className="card-body">
                <Bars testId="competitors" items={reasons.data.competitors.map((c) => ({ key: c.name, name: c.name, count: c.count }))} />
              </div>
            </div>
          </div>
        </>
      )}

      {people.data && people.data.length > 0 && reasons.data && reasons.data.reviewed > 0 && (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {people.data.map((p) => (
                <tr key={p.userId} data-testid="person-insight">
                  <Cells table={table} row={p} />
                  <td className="actions" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {digests.data && digests.data.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="row" style={{ gap: '0.5rem' }}>
              <GraduationCap size={17} aria-hidden /> {t('Weekly digests')}
            </h2>
          </div>
          <ul className="link-list">
            {digests.data.map((d) => (
              <li key={d.id}>
                <Link to={`/insights/digests/${d.id}`} data-testid="digest-link">
                  <span className="grow">
                    {t('Week of {date}: {about}', {
                      date: new Date(`${d.weekStart}T12:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }),
                      about: d.about,
                    })}
                  </span>
                  <Pill>{d.subject === 'TEAM' ? t('Team') : t('Personal')}</Pill>
                  <span className="subtle">{tp(d.callCount, '{n} call', '{n} calls')}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function DigestPage() {
  const { id = '' } = useParams();
  const digest = useLoad(() => api.getDigest(id), [id]);
  if (digest.error) return <Notice kind="error">{digest.error}</Notice>;
  if (!digest.data) return <Loading />;
  const d = digest.data;
  const section = (title: string, points: typeof d.wentWell, kind: 'good' | 'improve') => (
    <div className={`card digest-section ${kind}`}>
      <div className="card-header">
        <h2>{title}</h2>
      </div>
      <ol className="digest-points" data-testid={`digest-${kind}`}>
        {points.map((p) => (
          <li key={p.text}>
            <p>{p.text}</p>
            {p.calls.length > 0 && (
              <div className="row" style={{ gap: '0.4rem' }}>
                {p.calls.map((c) => (
                  <Link key={c.id} to={`/calls/${c.id}`} className="chip-button">
                    {c.label}, {when(c.startedAt)}
                  </Link>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
  return (
    <div className="stack">
      <Link to="/insights" className="back-link">
        ← {t('Insights')}
      </Link>
      <PageHeader
        title={t('Week of {date}', {
          date: new Date(`${d.weekStart}T12:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
        })}
        description={
          d.subject === 'TEAM'
            ? t('Team digest: {about}, from {calls}. Coaching, not grading.', { about: d.about, calls: tp(d.callCount, '{n} reviewed sales call', '{n} reviewed sales calls') })
            : t('Your digest, from {calls}. Coaching, not grading.', { calls: tp(d.callCount, '{n} reviewed sales call', '{n} reviewed sales calls') })
        }
      />
      {section(t('Going well'), d.wentWell, 'good')}
      {section(t('To improve'), d.toImprove, 'improve')}
      <p className="subtle" style={{ fontSize: '0.83rem' }}>
        {t('Written by AI ({model}) from the week’s reviews.', { model: d.model ?? '' })}
      </p>
    </div>
  );
}
